import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Title, useNotify } from "react-admin";

import { adminApi } from "../dataProvider";

// ── типы ответов ─────────────────────────────────────────────────────────────
type ProfitRow = {
  reference: string;
  amount_rub: number | null;
  broker_rate: number | null;
  client_rate: number | null;
  spread: number | null;
  margin_usdt: number;
  fee_usdt: number;
  created_at: string | null;
};
type ProfitResp = {
  date_from: string;
  date_to: string;
  rows: ProfitRow[];
  totals: { orders: number; rub: number; margin_usdt: number; fee_usdt: number; profit_usdt: number };
};
type EnergyBucket = {
  by_resource: { resource: string; rentals: number; quantity: number; cost_trx: number }[];
  total_trx: number;
  total_rentals: number;
};
type EnergyResp = { date_from: string; date_to: string; own?: EnergyBucket; broker?: EnergyBucket };
type PromoRow = {
  id: string;
  code: string;
  markup_pct: number;
  users_count: number;
  withdrawals_count: number;
  total_rub: number;
  total_margin_usdt: number;
  total_fee_usdt: number;
};
type TrafficRow = {
  code: string;
  deposits_count: number;
  deposits_rub: number;
  withdrawals_count: number;
  withdrawals_usdt: number;
};
type TrafficResp = { date_from: string; date_to: string; rows: TrafficRow[] };

// ── helpers ──────────────────────────────────────────────────────────────────
function monthStart(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function today(): string {
  return new Date().toISOString().slice(0, 10);
}
const rub = (v: number | null | undefined) =>
  v == null ? "—" : `${Number(v).toLocaleString("ru-RU")} ₽`;
const num = (v: number | null | undefined, digits = 2) =>
  v == null ? "—" : Number(v).toLocaleString("ru-RU", { minimumFractionDigits: digits, maximumFractionDigits: digits });
const RESOURCE_LABEL: Record<string, string> = { energy: "Энергия", bandwidth: "Bandwidth" };

function Metric({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
        {label}
      </Typography>
      <Typography variant={strong ? "h6" : "body1"} sx={{ fontWeight: strong ? 700 : 500 }}>
        {value}
      </Typography>
    </Box>
  );
}

// панель выбора периода — flex с переносом, кнопка не обрезается
function DateRange({
  from,
  to,
  setFrom,
  setTo,
  onApply,
  loading,
}: {
  from: string;
  to: string;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
  onApply: () => void;
  loading: boolean;
}) {
  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
      <TextField
        label="С"
        type="date"
        value={from}
        onChange={(e) => setFrom(e.target.value)}
        InputLabelProps={{ shrink: true }}
        size="small"
      />
      <TextField
        label="По"
        type="date"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        InputLabelProps={{ shrink: true }}
        size="small"
      />
      <Button variant="contained" onClick={onApply} disabled={loading} sx={{ whiteSpace: "nowrap" }}>
        {loading ? "Загрузка…" : "Применить"}
      </Button>
    </Box>
  );
}

// ── вкладка «Прибыль» ────────────────────────────────────────────────────────
function ProfitReport() {
  const notify = useNotify();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState<ProfitResp | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminApi<ProfitResp>(`/admin/reports/profit?date_from=${from}&date_to=${to}`));
    } catch (err) {
      notify(String((err as Error).message || "Ошибка загрузки отчёта"), { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [from, to, notify]);

  useEffect(() => {
    void load();
    // грузим при открытии вкладки; дальше — по кнопке «Применить»
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card>
      <CardContent>
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} onApply={() => void load()} loading={loading} />
        {data && (
          <>
            <Stack direction="row" spacing={4} sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}>
              <Metric label="Заявок" value={String(data.totals.orders)} />
              <Metric label="Оборот" value={rub(data.totals.rub)} />
              <Metric label="Маржа" value={`${num(data.totals.margin_usdt)} USDT`} />
              <Metric label="Комиссии" value={`${num(data.totals.fee_usdt)} USDT`} />
              <Metric label="Прибыль всего" value={`${num(data.totals.profit_usdt)} USDT`} strong />
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Заявка</TableCell>
                  <TableCell align="right">₽</TableCell>
                  <TableCell align="right">Курс брокера</TableCell>
                  <TableCell align="right">Курс клиента</TableCell>
                  <TableCell align="right">Спред</TableCell>
                  <TableCell align="right">Маржа, USDT</TableCell>
                  <TableCell align="right">Комиссия, USDT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((r, i) => (
                  <TableRow key={`${r.reference}-${i}`}>
                    <TableCell>{r.reference}</TableCell>
                    <TableCell align="right">{rub(r.amount_rub)}</TableCell>
                    <TableCell align="right">{num(r.broker_rate)}</TableCell>
                    <TableCell align="right">{num(r.client_rate)}</TableCell>
                    <TableCell align="right">{num(r.spread)}</TableCell>
                    <TableCell align="right">{num(r.margin_usdt)}</TableCell>
                    <TableCell align="right">{num(r.fee_usdt)}</TableCell>
                  </TableRow>
                ))}
                {data.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ color: "text.secondary" }}>
                      Нет заявок за период
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── вкладка «Энергия» (own / broker) ─────────────────────────────────────────
function EnergyReport({ bucket, note }: { bucket: "own" | "broker"; note: string }) {
  const notify = useNotify();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState<EnergyBucket | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await adminApi<EnergyResp>(
        `/admin/reports/energy?bucket=${bucket}&date_from=${from}&date_to=${to}`,
      );
      setData(resp[bucket] ?? null);
    } catch (err) {
      notify(String((err as Error).message || "Ошибка загрузки отчёта"), { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [bucket, from, to, notify]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card sx={bucket === "broker" ? { borderLeft: 3, borderColor: "warning.main" } : undefined}>
      <CardContent>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          {note}
        </Typography>
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} onApply={() => void load()} loading={loading} />
        {data && (
          <>
            <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
              <Metric label="Аренд" value={String(data.total_rentals)} />
              <Metric label="Итого, TRX" value={num(data.total_trx, 6)} strong />
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ресурс</TableCell>
                  <TableCell align="right">Аренд</TableCell>
                  <TableCell align="right">Объём</TableCell>
                  <TableCell align="right">Стоимость, TRX</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.by_resource.map((r) => (
                  <TableRow key={r.resource}>
                    <TableCell>{RESOURCE_LABEL[r.resource] || r.resource}</TableCell>
                    <TableCell align="right">{r.rentals}</TableCell>
                    <TableCell align="right">{r.quantity.toLocaleString("ru-RU")}</TableCell>
                    <TableCell align="right">{num(r.cost_trx, 6)}</TableCell>
                  </TableRow>
                ))}
                {data.by_resource.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: "text.secondary" }}>
                      Нет аренд за период
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── вкладка «Промокоды» (агрегаты за всё время) ──────────────────────────────
function PromoReport() {
  const notify = useNotify();
  const [data, setData] = useState<PromoRow[] | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminApi<PromoRow[]>("/admin/promo-report"));
    } catch (err) {
      notify(String((err as Error).message || "Ошибка загрузки отчёта"), { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card>
      <CardContent>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Итоги по промокодам за всё время: сколько клиентов привязано, объём и наша прибыль (маржа + комиссия).
        </Typography>
        {loading && !data && <Typography variant="body2">Загрузка…</Typography>}
        {data && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Код</TableCell>
                <TableCell align="right">Наценка</TableCell>
                <TableCell align="right">Привязано</TableCell>
                <TableCell align="right">Выводов</TableCell>
                <TableCell align="right">Объём, ₽</TableCell>
                <TableCell align="right">Маржа, USDT</TableCell>
                <TableCell align="right">Комиссия, USDT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.code}</TableCell>
                  <TableCell align="right">{Number(r.markup_pct)}%</TableCell>
                  <TableCell align="right">{r.users_count}</TableCell>
                  <TableCell align="right">{r.withdrawals_count}</TableCell>
                  <TableCell align="right">{rub(r.total_rub)}</TableCell>
                  <TableCell align="right">{num(r.total_margin_usdt)}</TableCell>
                  <TableCell align="right">{num(r.total_fee_usdt)}</TableCell>
                </TableRow>
              ))}
              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: "text.secondary" }}>
                    Нет промокодов
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ── вкладка «Трафик по ботам» (пополнения/выводы в разрезе промокода=бота) ────
function TrafficReport() {
  const notify = useNotify();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState<TrafficResp | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminApi<TrafficResp>(`/admin/reports/traffic?date_from=${from}&date_to=${to}`));
    } catch (err) {
      notify(String((err as Error).message || "Ошибка загрузки отчёта"), { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [from, to, notify]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card>
      <CardContent>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Сколько пополнений (₽) и выводов (USDT) прошло через каждый бот/партнёра (= промокод) за период.
          «— наш бот» — трафик без промокода (основной бот).
        </Typography>
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} onApply={() => void load()} loading={loading} />
        {data && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Бот / промокод</TableCell>
                <TableCell align="right">Пополнений</TableCell>
                <TableCell align="right">Оборот, ₽</TableCell>
                <TableCell align="right">Выводов</TableCell>
                <TableCell align="right">Выведено, USDT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.code}>
                  <TableCell>{r.code}</TableCell>
                  <TableCell align="right">{r.deposits_count}</TableCell>
                  <TableCell align="right">{rub(r.deposits_rub)}</TableCell>
                  <TableCell align="right">{r.withdrawals_count}</TableCell>
                  <TableCell align="right">{num(r.withdrawals_usdt)}</TableCell>
                </TableRow>
              ))}
              {data.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>
                    Нет трафика за период
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ── страница с вкладками (каждый отчёт грузится только при открытии) ──────────
export function ReportsPage() {
  const [tab, setTab] = useState(0);
  return (
    <Box sx={{ maxWidth: 1000, mt: 2 }}>
      <Title title="Отчёты" />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        <Tab label="Прибыль" />
        <Tab label="Промокоды" />
        <Tab label="Трафик по ботам" />
        <Tab label="Энергия — наши" />
        <Tab label="Энергия — брокеру" />
      </Tabs>
      {tab === 0 && <ProfitReport />}
      {tab === 1 && <PromoReport />}
      {tab === 2 && <TrafficReport />}
      {tab === 3 && (
        <EnergyReport bucket="own" note="Аренда энергии/bandwidth на кастодиалы клиентов — наши расходы." />
      )}
      {tab === 4 && (
        <EnergyReport
          bucket="broker"
          note="Доливы на платёжный кош брокера — стоимость, которую предъявляем брокеру за период."
        />
      )}
    </Box>
  );
}
